import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  formsConfig,
  tabs,
} from '@constants/medicines/forms/medicine-new.config';
import { TabInterface } from '@interfaces/dynamic-form.interface';
import { FormDetailAction } from '@interfaces/form/form-detail';
import { SelectOption } from '@interfaces/input-select-option.interface';
import {
  MedicineAdminRoute,
  MedicineEditEmpty,
  MedicineForm,
  MedicineInteraction,
  MedicineInteractionText,
} from '@interfaces/medicine/medicine.interface';
import { FormInterface } from '@interfaces/patient/patient-edit.interface';
import { CallsService } from '@services/api/calls.service';
import { FormService } from '@services/form.service';
import { InstitutionService } from '@services/institution.service';
import { MedicinesService } from '@services/medicines.service';
import { InteractionService } from '@services/medicines/interaction.service';
import { LoadingService } from '@services/loading.service';
import { Subscription, skipWhile, take } from 'rxjs';
import { StockService } from '@services/stock.service';

@Component({
  selector: 'app-medicine-new',
  templateUrl: './medicine-new.component.html',
  styleUrls: ['./medicine-new.component.scss'],
})
export class MedicineNewComponent {
  public isEdit: boolean = false;

  public forms: FormInterface = {};
  public tabs: TabInterface[] = tabs;
  private formsConfig = formsConfig;

  private subs: Subscription[] = [];

  //medicines
  public activeIngredientsOptions: SelectOption[] = [];
  public medicineAdminRouteList: MedicineAdminRoute[];
  public medicineInteractionList: MedicineInteraction[];
  public medicineInteractionListText: MedicineInteractionText[];
  public medicineInfo: MedicineForm;
  public medicines: MedicineForm;
  public medicineId: string | null;

  public tabQueryParam: string | null;
  public selectedTabIndex: number;

  public formChanged: boolean = true;

  public collapsedProfile: boolean = false;

  public suppliersOptions: SelectOption[] = [];

  @ViewChild('saveButton') saveButton: any;

  public onDestroy: () => void;
  public onMedicineId: (patId: string | null) => void;
  public onMedicineUpdated: (medicine: MedicineForm, medicineId: string | null) => void;

  constructor(
    private route: ActivatedRoute,
    private medicineService: MedicinesService,
    private interactionService: InteractionService,
    private formService: FormService,
    private institutionService: InstitutionService,
    private calls: CallsService,
    private loadingService: LoadingService,
    private stockService: StockService,
  ) {}

  ngOnInit(): void {
    this.medicineService.clearCurrentMedicine();
    this.getSelectedTab();
    this.initForms();
    this.getMedicineDefaults();
    this.getMedicineAdministrationOptions();
    this.getActiveIngredientsOptions();
    this.getSuppliersOptions();
    this.medicineInfo = this.medicines;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  getSelectedTab(): void {
    this.route.queryParamMap.subscribe((query) => {
      this.tabQueryParam = query.get('tab');
      this.selectedTabIndex = this.tabQueryParam
        ? parseInt(this.tabQueryParam)
        : 0;
    });
  }


  setMedicineAdminRoute(): void {
    //get the medicines admin routes & set the admin route option from the backend
    if (this.medicineAdminRouteList) {
      let optionsAdminRoute = this.medicineAdminRouteList.map((admin) => {
        return this.medicineService.transformMedicineIAdminRouteToSelectOption(
          admin
        );
      });

      // @ts-ignore
      if ( this.forms['general']?.schema[0]?.childs[6] ) {
        this.forms['general'].schema[0].childs[6].options = optionsAdminRoute;
      }
    }
  }
  
  setActiveIngredientsOptions(): void {
    if (this.activeIngredientsOptions.length > 0) {

      // @ts-ignore
      if ( this.forms['general']?.schema[2]?.childs[0] ) {
        this.forms['general'].schema[2].childs[0].options = this.activeIngredientsOptions;
      }
    }
  }

  setInteractionFormInfo(medicineInfo: MedicineForm): void {
    this.forms['interactions'].form.patchValue(medicineInfo.interactions);
    this.forms['interactions'].schema[1].options =
      this.medicineService.convertToInteraction(
        medicineInfo.interactions.interactions
      );
  }

  getMedicineDefaults() {
    this.medicineService.getMedicineEdit(0).then(data => {
      this.medicineService.currentMedicine
        .pipe(
            skipWhile(medicine => medicine instanceof MedicineEditEmpty),
            take(1)
        )
    })
  }

  // Admin route is a endpoint that you call when you need to get the options
  // list for "vía de administración" (of medication)
  getMedicineAdministrationOptions(): void {
    this.subs.push(
      this.medicineService.medicineAdminRouteList.subscribe((adminRoutes) => {
        this.medicineAdminRouteList = adminRoutes.map((adminRoute) =>
          this.medicineService.transformMedicineAdminRouteAPItoApp(adminRoute)
        );
        this.setMedicineAdminRoute();
      })
    );
    this.medicineService.getMedicineAdminRoute(
      false,
      false,
      0
    );
  }

  getActiveIngredientsOptions(): void {
    this.subs.push(
      this.medicineService.getActiveIngredientsList().subscribe((activeIngredients) => {
        this.activeIngredientsOptions = activeIngredients.map((activeIngredient) => {
          return {
            label: activeIngredient.Nombre,
            value: activeIngredient.Id,
          };
        });
        this.setActiveIngredientsOptions();
      })
    );
  }

  getSuppliersOptions(): void {
    this.subs.push(
      this.stockService.getSuppliers().subscribe((suppliers) => {
        console.log(suppliers);
        this.suppliersOptions = suppliers.map((supplier) => {
          return { label: supplier.Name, value: supplier.Id };
        });

        console.log(this.suppliersOptions);
        this.setSuppliersOptions();
      })
    );
  }

  setSuppliersOptions(): void {
    // @ts-ignore
    if ( this.forms['general']?.schema[0]?.childs[4] ) {
      this.forms['general'].schema[0].childs[4].options = this.suppliersOptions;
    }
  }

  initForms(): void {
    this.tabs.forEach((tab) => {
      const formName = tab.form;

      const result = this.formService.buildForm(this.formsConfig[formName]);
      this.forms[formName] = {
        form: result[0],
        schema: result[1],
      };
    });

    this.forms['general'].form.markAllAsTouched();
  }

  updateMedicineEdit(): void {
    // Check if classification is ok
    const classificationOk = Object.keys(this.forms['general'].form.value.classification)
    .some(key => this.forms['general'].form.value.classification[key] === true);

    if ( !classificationOk ) {
      // If classification is not ok, set custom style to the field
      this.forms['general'].schema[4].customClass = [...<[]>this.forms['general'].schema[4].customClass || [], 'error']
      this.calls.openSnack('Existen errores en el formulario', 'X', 5000);
    } else if ( this.forms['general'].form.valid ) {
      const medicine = this.getFormInfo();

      this.medicineService.updateMedicine(
        medicine,
        this.medicineId !== null ? +this.medicineId : 0,
        undefined
      ).then((medicineId) => {
        this.medicineId = medicineId.toString();
        this.formChanged = false;
      })
      .catch();

    } else {
      this.calls.openSnack('Existen errores en el formulario', 'X', 5000);
    }
  }

  getFormInfo(): MedicineForm {
    const object: { [key: string]: any } = {};
    this.tabs.forEach((tab) => {
      const formName = tab.form;
      object[formName] = this.forms[formName].form.value;
    });

    const { general, ...otherProperties } = object;
    const res: MedicineForm = { ...general, ...otherProperties };

    // Get active ingredients from the backend
    // @ts-ignore
    const activeIngredientsIds = res.activeIngredients.activeIngredients;
    const activeIngredients = this.activeIngredientsOptions.filter(option => activeIngredientsIds.includes(option.value));
    res.activeIngredients = activeIngredients.map(item => { return { Id: item.value, Nombre: item.label, DMARC: false, Estupefaciente: false, Psicotropo: false } });

    return res;
  }

  selectedIndex: number = 0;
  indexChange(idx: number) {
      this.selectedIndex = idx;
  }

  onSearchButtonClick(value: string) {
    this.loadingService.start('Cargando medicina...');

    this.medicineService.getMedicineEdit(0, Number(value))
    .then(data => {
      const medicine = this.medicineService.transformMedicineEditToForm(data as any, true);
      medicine.activeIngredients = {
        // @ts-ignore
        activeIngredients: medicine.activeIngredients.map(item => item.Id)
      }
      this.forms['general'].form.patchValue(medicine, { emitEvent: false });

      this.loadingService.stop();
    }, error => {
      this.loadingService.stop();
    })

  }
}
